import React, { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { ApiHook } from "../../hooks/apiHook";
import CurrencyConverter from "../../Currency/CurrencyConverter";
import InvestmentReportSkeleton from "./InvestmentReportSkeleton";
import "./InvestmentReport.css";
import {
  formatDate,
  formatDateWithoutDate,
  formatDateWithoutTime,
} from "../../utils/formateDate";

const InvestmentReport = () => {
  const { t } = useTranslation();
  const [selectedPeriod, setSelectedPeriod] = useState("all"); // reserved for future filtering
  // Earnings (interest) table pagination
  const [earningsPage, setEarningsPage] = useState(1);
  const [earningsPerPage, setEarningsPerPage] = useState(10);
  // Investment summary table pagination
  const [invPage, setInvPage] = useState(1);
  const [invPerPage, setInvPerPage] = useState(10);

  // Filters
  const [invSearchInput, setInvSearchInput] = useState("");
  const [invSearch, setInvSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [contractFilter, setContractFilter] = useState("all");
  const [tokenFilter, setTokenFilter] = useState("all");
  const [earningsMonthFilter, setEarningsMonthFilter] = useState("all");
  const [earningsInvFilter, setEarningsInvFilter] = useState("all");

  const userSelectedCurrency = useSelector(
    (state) => state.user?.selectedCurrency
  );
  const conversionFactor = useSelector(
    (state) => state?.user?.conversionFactor
  );

  // Server-side investments & interest endpoints
  // Expected investments API response (ggfc-investments):
  // {
  //   data: [ { id, amount, tokenType, startDate, endDate, daysRemaining, status, isMatured, stakingPeriod, "contracts.name" } ],
  //   currentPage: 1,
  //   perPage: 10,
  //   totalPages: 5,
  //   total: 47,
  //   filters: { contracts: [..], tokens:[..], statuses:[..] }
  // }
  const investmentsRes = ApiHook.CallInvestments(
    invPage,
    invPerPage,
    invSearch,
    statusFilter === "all" ? "" : statusFilter,
    contractFilter === "all" ? "" : contractFilter,
    tokenFilter === "all" ? "" : tokenFilter
  );
  const investmentsData = investmentsRes?.data;
  const loadingInvestments = investmentsRes?.isLoading;

  // Expected interest API response (ggfc-investment-interest):

  const interestRes = ApiHook.CallInvestmentInterest(
    earningsPage,
    earningsPerPage,
    invSearchInput,
    earningsMonthFilter === "all" ? "" : earningsMonthFilter
  );
  const interestData = interestRes?.data;
  const loadingInterest = interestRes?.isLoading;

  const formatAmount = (amount) => {
    return CurrencyConverter(amount, conversionFactor);
  };

  const getStatusBadge = (investment) => {
    let statusClass, statusText;

    if (investment.isMatured) {
      statusClass = "success";
      statusText = "Matured";
    } else if (investment.status === "active") {
      statusClass = "primary";
      statusText = "Active";
    } else {
      statusClass = "warning";
      statusText = investment.status;
    }

    return <span className={`badge bg-${statusClass}`}>{statusText}</span>;
  };

  const calculateProgress = (startDate, endDate, daysRemaining) => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    const totalDays = Math.ceil((end - start) / (1000 * 60 * 60 * 24));
    const elapsedDays = totalDays - daysRemaining;

    if (daysRemaining <= 0) return 100;
    if (elapsedDays <= 0) return 0;

    return Math.round((elapsedDays / totalDays) * 100);
  };

  const getContractName = (investment) => {
    return investment["contracts.name"] || `${investment.stakingPeriod} months`;
  };

  // Derive lists for filters (fallback to client building if API not yet providing)
  const investmentsArray = investmentsData?.data || [];

  // Derive contract list for filter
  const contractOptions = useMemo(() => {
    if (investmentsData?.filters?.contracts)
      return investmentsData?.filters?.contracts;
    const set = new Set();
    investmentsArray.forEach((i) => {
      const name = getContractName(i);
      if (name) set.add(name);
    });
    return Array.from(set).sort();
  }, [investmentsData]);

  console.log(contractOptions);

  // Filtering investments

  const invTotalPages = investmentsData?.totalPages || 1;
  const paginatedInvestments = investmentsArray; // server already paginated

  // Reset page when filters change
  React.useEffect(() => {
    setInvPage(1);
  }, [invSearch, statusFilter, contractFilter, tokenFilter]);

  const earningsTotalPages = interestData?.totalPages || 1;
  const paginatedEarnings = interestData?.data; // server paginated

  const handleChangePerPage = (e) => {
    setEarningsPerPage(Number(e.target.value));
    setEarningsPage(1);
  };

  const gotoPage = (page) => {
    if (page < 1 || page > earningsTotalPages) return;
    setEarningsPage(page);
  };

  // Investment pagination handlers
  const handleInvPerPage = (e) => {
    setInvPerPage(Number(e.target.value));
    setInvPage(1);
  };
  const gotoInvPage = (page) => {
    if (page < 1 || page > invTotalPages) return;
    setInvPage(page);
  };

  // Month options (YYYY-MM)
const earningsMonthOptions = [
  { key: 1, value: "Jan" },
  { key: 2, value: "Feb" },
  { key: 3, value: "Mar" },
  { key: 4, value: "Apr" },
  { key: 5, value: "May" },
  { key: 6, value: "Jun" },
  { key: 7, value: "Jul" },
  { key: 8, value: "Aug" },
  { key: 9, value: "Sep" },
  { key: 10, value: "Oct" },
  { key: 11, value: "Nov" },
  { key: 12, value: "Dec" },
];


  // Investment options for earnings filter
  const earningsInvestmentOptions = interestData?.filters?.investments || [];

  // if (loadingInvestments || loadingInterest) {
  //   return <InvestmentReportSkeleton />;
  // }
  return (
    <div className="ewallet_table_section">
      <div className="investment-report">
        <>
          <div className="card border-0 shadow-sm mb-4">
            <div className="card-header bg-white py-3">
              <div className="d-flex flex-column flex-lg-row gap-3 justify-content-between align-items-lg-end">
                <div className="d-flex flex-wrap gap-2 align-items-end">
                  <div>
                    <label className="form-label small text-muted mb-1">
                      Search
                    </label>
                    <input
                      value={invSearch}
                      onChange={(e) => setInvSearch(e.target.value)}
                      placeholder="INV-0001"
                      className="form-control form-control-sm"
                    />
                  </div>
                  <div>
                    <label className="form-label small text-muted mb-1">
                      Status
                    </label>
                    <select
                      value={statusFilter}
                      onChange={(e) => setStatusFilter(e.target.value)}
                      className="form-select form-select-sm"
                    >
                      <option value="all">All</option>
                      <option value="active">Active</option>
                      <option value="matured">Matured</option>
                      <option value="pending">Pending</option>
                    </select>
                  </div>
                  <div>
                    <label className="form-label small text-muted mb-1">
                      Contract
                    </label>
                    <select
                      value={contractFilter}
                      onChange={(e) => setContractFilter(e.target.value)}
                      className="form-select form-select-sm"
                    >
                      <option value="all">All</option>
                      {contractOptions.map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="pt-2 d-flex">
                    <button
                      type="button"
                      style={{ backgroundColor: "#bcbdbeff" }}
                      className="btn btn-sm btn-outline-secondary mt-auto"
                      onClick={() => {
                        setInvSearch("");
                        setStatusFilter("all");
                        setContractFilter("all");
                        setTokenFilter("all");
                        setInvPage(1);
                      }}
                    >
                      Reset
                    </button>
                  </div>
                </div>
                <div className="d-flex align-items-center gap-2 ms-lg-auto">
                  <label className="text-muted small mb-0">Rows:</label>
                  <select
                    value={invPerPage}
                    onChange={handleInvPerPage}
                    className="form-select form-select-sm w-auto"
                  >
                    {[10, 25, 50, 100].map((n) => (
                      <option key={n} value={n}>
                        {n}
                      </option>
                    ))}
                  </select>
                  <span className="text-muted small">
                    Page {invPage} / {invTotalPages}
                  </span>
                </div>
              </div>
            </div>
            {loadingInvestments ? (
              <InvestmentReportSkeleton />
            ) : (
              <>
                {!investmentsArray || investmentsArray.length === 0 ? (
                  <>
                    <div className="investment-report-no-data">
                      <div
                        className=" justify-content-center align-items-center"
                        style={{ height: "400px", marginTop: "56px" }}
                      >
                        <div className="text-center">
                          <div className="mb-4">
                            <i
                              className="fas fa-chart-line text-muted"
                              style={{
                                fontSize: "4rem",
                                opacity: "0.3",
                                marginTop: "20px",
                              }}
                            ></i>
                          </div>
                          <h5 className="text-muted mb-3">
                            No Investment Data Available
                          </h5>
                          <p className="text-muted mb-4">
                            You haven't made any GGFC token investments yet.
                            <br />
                            Start investing to see your portfolio performance
                            here.
                          </p>
                        </div>
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="card-body p-0">
                      <div className="table-responsive">
                        <table className="table table-hover mb-0">
                          <thead className="table-light">
                            <tr>
                              <th className="ps-4">Investment</th>
                              <th>Amount</th>
                              <th>Contract</th>
                              <th>Token</th>
                              <th>Start</th>
                              <th>End</th>
                              <th>Days Left</th>
                              <th>Status</th>
                              <th className="pe-4">Progress</th>
                            </tr>
                          </thead>
                          <tbody>
                            {paginatedInvestments.map((inv) => {
                              const progress = calculateProgress(
                                inv.startDate,
                                inv.endDate,
                                inv.daysRemaining
                              );
                              return (
                                <tr key={inv.id}>
                                  <td className="ps-4">
                                    <span className="fw-medium">
                                      INV-{inv.id.toString().padStart(4, "0")}
                                    </span>
                                  </td>
                                  <td>
                                    {formatAmount(inv.amount)} {inv.tokenType}
                                  </td>
                                  <td>{getContractName(inv)}</td>
                                  <td>
                                    <span className="badge bg-light text-dark">
                                      {inv.tokenType}
                                    </span>
                                  </td>
                                  <td>
                                    {formatDateWithoutTime(inv.startDate)}
                                  </td>
                                  <td>{formatDateWithoutTime(inv.endDate)}</td>
                                  <td>
                                    <span
                                      className={
                                        inv.daysRemaining > 0
                                          ? "text-warning"
                                          : "text-success"
                                      }
                                    >
                                      {inv.daysRemaining > 0
                                        ? `${inv.daysRemaining} days`
                                        : "Matured"}
                                    </span>
                                  </td>
                                  <td>{getStatusBadge(inv)}</td>
                                  <td className="pe-4">
                                    <div className="d-flex align-items-center">
                                      <div
                                        className="progress flex-grow-1 me-2"
                                        style={{ height: 6 }}
                                      >
                                        <div
                                          className={`progress-bar ${
                                            inv.isMatured
                                              ? "bg-success"
                                              : "bg-primary"
                                          }`}
                                          style={{ width: progress + "%" }}
                                        ></div>
                                      </div>
                                      <small className="text-muted">
                                        {progress}%
                                      </small>
                                    </div>
                                  </td>
                                </tr>
                              );
                            })}
                            {paginatedInvestments.length === 0 && (
                              <tr>
                                <td
                                  colSpan={9}
                                  className="text-center py-4 text-muted"
                                >
                                  No investments found
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </>
                )}
              </>
            )}
            <div className="d-flex flex-column flex-md-row justify-content-between align-items-center gap-2 px-3 py-3 border-top table-pagination">
              <div className="small text-muted">
                Page {invPage} of {invTotalPages} | Showing{" "}
                {paginatedInvestments.length} records
              </div>
              <div className="d-flex align-items-center gap-2">
                <button
                  className="btn btn-sm btn-outline-secondary"
                  disabled={invPage === 1}
                  onClick={() => gotoInvPage(1)}
                >
                  &laquo;
                </button>
                <button
                  className="btn btn-sm btn-outline-secondary"
                  disabled={invPage === 1}
                  onClick={() => gotoInvPage(invPage - 1)}
                >
                  &lsaquo;
                </button>
                <span className="small fw-medium">
                  {invPage} / {invTotalPages}
                </span>
                <button
                  className="btn btn-sm btn-outline-secondary"
                  disabled={invPage === invTotalPages}
                  onClick={() => gotoInvPage(invPage + 1)}
                >
                  &rsaquo;
                </button>
                <button
                  className="btn btn-sm btn-outline-secondary"
                  disabled={invPage === invTotalPages}
                  onClick={() => gotoInvPage(invTotalPages)}
                >
                  &raquo;
                </button>
              </div>
            </div>
          </div>
        </>

        {/* Summary Table */}

        <div className="card border-0 shadow-sm">
          <div className="card-header bg-white py-3">
            <div className="d-flex flex-column flex-md-row gap-2 justify-content-between align-items-md-center">
              <h6 className="mb-0">Monthly Interest Earnings</h6>
              <div className="d-flex flex-wrap gap-2">
                <div>
                  <input
                    value={invSearchInput}
                    onChange={(e) => setInvSearchInput(e.target.value)}
                    placeholder="INV-0001"
                    className="form-control form-control-sm"
                  />
                </div>
                <div>
                  <select
                    value={earningsMonthFilter}
                    onChange={(e) => {
                      setEarningsMonthFilter(e.target.value);
                      setEarningsPage(1);
                    }}
                    className="form-select form-select-sm"
                  >
                    <option value="all">All Months</option>
                    {earningsMonthOptions.map((m) => (
                      <option key={m.key} value={m.key}>
                        {m.value}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="d-flex">
                  <button
                    type="button"
                    style={{ backgroundColor: "#bcbdbeff" }}
                    className="btn btn-sm btn-outline-secondary"
                    onClick={() => {
                      setInvSearchInput("");
                      setEarningsMonthFilter("all");
                      setEarningsPage(1);
                    }}
                  >
                    Reset
                  </button>
                </div>
              </div>
              <div className="d-flex align-items-center gap-2 small">
                <label className="text-muted mb-0">Rows:</label>
                <select
                  value={earningsPerPage}
                  onChange={handleChangePerPage}
                  className="form-select form-select-sm w-auto"
                >
                  {[10, 25, 50, 100].map((n) => (
                    <option key={n} value={n}>
                      {n}
                    </option>
                  ))}
                </select>
                <span className="text-muted">
                  Page {earningsPage} / {earningsTotalPages}
                </span>
              </div>
            </div>
          </div>
          <div className="card-body p-0">
            <div className="table-responsive">
              <table className="table table-sm table-striped table-hover mb-0 align-middle earnings-table">
                <thead className="table-light">
                  <tr>
                    <th className="ps-4">Investment</th>
                    <th>Month</th>
                    <th className="text-end">Token</th>
                  </tr>
                </thead>

                {loadingInterest ? (
                  <InvestmentReportSkeleton />
                ) : (
                  <tbody>
                    {paginatedEarnings.map((row) => {
                      const invId = row.investmentId;
                      return (
                        <tr key={row.id} className="earnings-row">
                          <td className="ps-4" data-label="Investment">
                            <span className="fw-medium">
                              INV-{String(invId).padStart(4, "0")}
                            </span>
                          </td>
                          <td data-label="Month">
                            {formatDateWithoutDate(row.dateAdded)}
                          </td>
                          <td className="text-end">
                            {formatAmount(row.amount)}
                          </td>
                        </tr>
                      );
                    })}
                    {paginatedEarnings.length === 0 && (
                      <tr>
                        <td colSpan={6} className="text-center py-4 text-muted">
                          No earnings data
                        </td>
                      </tr>
                    )}
                  </tbody>
                )}
              </table>
            </div>
            {/* Pagination Controls */}
            <div className="d-flex flex-column flex-md-row justify-content-between align-items-center gap-2 px-3 py-3 border-top table-pagination">
              <div className="small text-muted">
                Page {earningsPage} of {earningsTotalPages} | Showing{" "}
                {paginatedEarnings?.length ?? 0} records
              </div>
              <div className="d-flex align-items-center gap-2">
                <button
                  className="btn btn-sm btn-outline-secondary"
                  disabled={earningsPage === 1}
                  onClick={() => gotoPage(1)}
                >
                  &laquo;
                </button>
                <button
                  className="btn btn-sm btn-outline-secondary"
                  disabled={earningsPage === 1}
                  onClick={() => gotoPage(earningsPage - 1)}
                >
                  &lsaquo;
                </button>
                <span className="small fw-medium">
                  {earningsPage} / {earningsTotalPages}
                </span>
                <button
                  className="btn btn-sm btn-outline-secondary"
                  disabled={earningsPage === earningsTotalPages}
                  onClick={() => gotoPage(earningsPage + 1)}
                >
                  &rsaquo;
                </button>
                <button
                  className="btn btn-sm btn-outline-secondary"
                  disabled={earningsPage === earningsTotalPages}
                  onClick={() => gotoPage(earningsTotalPages)}
                >
                  &raquo;
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InvestmentReport;
